import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import type { SourceRef } from '../lib/api'

/**
 * The knowledge-base documents a chat answer consulted, in an accessible modal dialog: one section
 * per KB, one row per FILE (never per excerpt), each a breadcrumb `source › folders › file`.
 *
 * "Consulted", not "cited". The passerelle records what the search tools RETURNED to the agent,
 * which is knowable; whether the model leaned on a given excerpt is not, and inline `[1]` markers
 * are deliberately deferred rather than faked.
 *
 * Nothing here decides what may be seen: the list arrives already ACL-trimmed (bid) or
 * group-gated and location-masked (corporate) by the backend. A row links out to SharePoint,
 * which enforces its own rights on the click.
 *
 * Same dialog mechanics as HistoryModal (portal on <body>, focus on open, focus restored on
 * close, Escape, Tab trapped, background scroll locked).
 */
export function SourcesModal({ sources, onClose }: { sources: SourceRef[]; onClose: () => void }) {
  const { t } = useLang()
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  // Focus management: focus the close button on open, restore focus on close.
  // Also lock background page scroll while the modal is open.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus()
    }
  }, [])

  // Escape closes; Tab is trapped inside the dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const sections = [
    { kb: 'bid' as const, label: t('sources_bid'), items: sources.filter((s) => s.kb === 'bid') },
    { kb: 'corporate' as const, label: t('sources_corporate'), items: sources.filter((s) => s.kb === 'corporate') },
  ].filter((s) => s.items.length > 0)

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal sources-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sources-title"
        ref={dialogRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h2 id="sources-title">{t('sources_title')}</h2>
          <button ref={closeRef} className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {sections.map((section) => (
            <section className="src-section" key={section.kb}>
              <h3 className="src-kb">{section.label}</h3>
              <ul className="src-list">
                {section.items.map((src) => {
                  const key = rowKey(src)
                  const open = expanded.has(key)
                  return (
                    <li className="src-row" key={key}>
                      <div className="src-line">
                        {src.snippets.length > 0 && (
                          <button
                            className="src-chevron"
                            aria-expanded={open}
                            aria-label={t('sources_show_excerpts')}
                            onClick={() => toggle(key)}
                          >
                            {open ? '▾' : '▸'}
                          </button>
                        )}
                        <SourceLabel src={src} />
                        {src.hits > 1 && (
                          <span className="src-hits" title={`${src.hits} ${t('sources_excerpts')}`}>
                            ×{src.hits}
                          </span>
                        )}
                      </div>
                      {open && (
                        <ul className="src-snippets">
                          {src.snippets.map((snippet, i) => (
                            <li key={i}>{snippet}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * The breadcrumb itself — a link when the store recorded a URL, plain text otherwise (a document
 * uploaded straight into the bid has none, and neither does a masked corporate source). The full
 * path is in `title` because the row middle-truncates on a narrow panel.
 */
function SourceLabel({ src }: { src: SourceRef }) {
  const { t } = useLang()
  const trail = [src.source, ...src.folders].filter(Boolean)
  const full = [...trail, src.file].join(' › ')

  const body = (
    <>
      <span className="src-icon" aria-hidden="true">
        {fileGlyph(src.file)}
      </span>
      {trail.length > 0 && (
        <span className="src-trail">
          {trail.map((crumb, i) => (
            <span key={i}>
              {crumb}
              <span className="src-sep" aria-hidden="true">
                {' › '}
              </span>
            </span>
          ))}
        </span>
      )}
      <span className="src-file">{src.file}</span>
    </>
  )

  if (!src.url) {
    return (
      <span className="src-label" title={`${full} — ${t('sources_no_link')}`}>
        {body}
      </span>
    )
  }
  return (
    <a
      className="src-label src-link"
      href={src.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${full} — ${t('sources_opens_new_tab')}`}
    >
      {body}
      <span className="src-ext" aria-hidden="true">
        ↗
      </span>
    </a>
  )
}

/** A stable row key: the URL when there is one, else the breadcrumb (which is what deduplicated it). */
function rowKey(src: SourceRef): string {
  return `${src.kb}|${src.url || [src.source, ...src.folders, src.file].join('/')}`
}

/** A file-type glyph from the extension. Decorative — the extension is already in the name. */
function fileGlyph(file: string): string {
  const ext = file.slice(file.lastIndexOf('.') + 1).toLowerCase()
  if (ext === 'pdf') return '📕'
  if (ext === 'doc' || ext === 'docx' || ext === 'odt') return '📘'
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return '📗'
  if (ext === 'ppt' || ext === 'pptx') return '📙'
  if (ext === 'md' || ext === 'txt') return '📝'
  return '📄'
}
